import {useEffect, useState} from "react";

// Öffentliche Portal-API (ohne Auth, CORS *). ID-Server und Key kommen immer von hier,
// da sie im Portal konfigurierbar sind – niemals hart codieren.
export const RUSTDESK_API = "https://portal.dockline.digital/api/v1/public/support/rustdesk";

export type Platform = "windows" | "mac-arm" | "mac-intel";

export interface RustdeskDownload {
    available: boolean;
    version?: string;
    sizeBytes?: number;
    filename?: string;
    url: string;
}

export interface RustdeskInfo {
    idServer?: string;
    publicKey?: string;
    downloads: Partial<Record<Platform, RustdeskDownload>>;
}

export type RustdeskState =
    | { status: "loading" }
    | { status: "ok"; info: RustdeskInfo }
    | { status: "error" };

// Wird verlinkt, wenn die API nicht erreichbar ist.
export const fallbackDownloadUrl = (platform: Platform) => `${RUSTDESK_API}/download/${platform}`;

const PLATFORMS: Platform[] = ["windows", "mac-arm", "mac-intel"];
const API_TIMEOUT_MS = 6000;

const nonEmptyString = (v: unknown): string | undefined =>
    typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;

function parseInfo(raw: any): RustdeskInfo {
    const downloads: RustdeskInfo["downloads"] = {};
    for (const p of PLATFORMS) {
        const d = raw?.downloads?.[p];
        if (!d || typeof d !== "object") continue;
        const url = nonEmptyString(d.url);
        downloads[p] = {
            available: d.available === true,
            version: nonEmptyString(d.version),
            sizeBytes: typeof d.sizeBytes === "number" && d.sizeBytes > 0 ? d.sizeBytes : undefined,
            filename: nonEmptyString(d.filename),
            url: url && url.startsWith("https://") ? url : fallbackDownloadUrl(p),
        };
    }
    return {
        idServer: nonEmptyString(raw?.idServer),
        publicKey: nonEmptyString(raw?.publicKey),
        downloads,
    };
}

export function useRustdeskInfo(): RustdeskState {
    const [state, setState] = useState<RustdeskState>({status: "loading"});

    useEffect(() => {
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), API_TIMEOUT_MS);
        let cancelled = false;

        (async () => {
            try {
                const res = await fetch(RUSTDESK_API, {
                    signal: controller.signal,
                    credentials: "omit",
                    referrerPolicy: "no-referrer",
                    headers: {Accept: "application/json"},
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const info = parseInfo(await res.json());
                if (!cancelled) setState({status: "ok", info});
            } catch {
                if (!cancelled) setState({status: "error"});
            } finally {
                window.clearTimeout(timer);
            }
        })();

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
            controller.abort();
        };
    }, []);

    return state;
}

export function formatSize(bytes?: number): string | undefined {
    if (!bytes) return undefined;
    const mb = bytes / 1_000_000;
    return `${new Intl.NumberFormat("de-DE", {maximumFractionDigits: mb < 10 ? 1 : 0}).format(mb)} MB`;
}

/* ---------- Geräteerkennung (nur lokal, es wird nichts übertragen) ---------- */

type UAData = {
    platform?: string;
    mobile?: boolean;
    getHighEntropyValues?: (hints: string[]) => Promise<{ architecture?: string }>;
};

const uaData = (): UAData | undefined => (navigator as any).userAgentData;

export type DetectedOS = "windows" | "mac" | "mobile" | "other";

export function isMobileDevice(): boolean {
    if (uaData()?.mobile) return true;
    const ua = navigator.userAgent;
    if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
    // iPadOS meldet sich als „Macintosh“, hat aber einen Touchscreen.
    return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

export function detectOS(): DetectedOS {
    if (isMobileDevice()) return "mobile";
    const platform = uaData()?.platform?.toLowerCase() ?? "";
    const ua = navigator.userAgent;
    if (platform === "windows" || /Windows/i.test(ua)) return "windows";
    if (platform === "macos" || /Macintosh|Mac OS X/i.test(ua)) return "mac";
    return "other";
}

function webglRenderer(): string | undefined {
    try {
        const canvas = document.createElement("canvas");
        const gl = (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
        if (!gl) return undefined;
        const ext = gl.getExtension("WEBGL_debug_renderer_info");
        const renderer = gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER);
        gl.getExtension("WEBGL_lose_context")?.loseContext();
        return typeof renderer === "string" ? renderer : undefined;
    } catch {
        return undefined;
    }
}

export type MacVariant = "mac-arm" | "mac-intel";

export async function detectMacVariant(): Promise<MacVariant> {
    try {
        const values = await uaData()?.getHighEntropyValues?.(["architecture"]);
        if (values?.architecture === "arm") return "mac-arm";
        if (values?.architecture === "x86") return "mac-intel";
    } catch {
        // Client Hints nicht verfügbar oder verweigert → weiter mit WebGL.
    }
    const renderer = webglRenderer();
    if (renderer && /intel|amd|radeon/i.test(renderer)) return "mac-intel";
    return "mac-arm";
}

export const SUPPORT_PHONE = {display: "+49 152 53819840", href: "tel:+4915253819840"};
export const SUPPORT_MAIL = "info@dockline.digital";
